<template>
  <v-container
    id="login"
    class="justify-center mt-10 mt-sm-0 pt-3 pt-sm-0 pb-3 pb-sm-0"
    tag="section"
    :fluid="
      $vuetify.breakpoint.name != 'xl' && $vuetify.breakpoint.name != 'lg'
    "
  >
    <v-row justify="center">
      <v-col lg="12" md="12" sm="12" xl="7" class="pl-2 pr-2 pt-0 pb-0">
        <v-card class="elevation-4 pl-2 pr-2">
          <v-row>
            <v-col
              lg="5"
              md="5"
              class="d-none d-md-flex pa-0 full-height left-panel"
            >
              <div></div>
            </v-col>
            <v-col lg="7" md="7" class="pl-md-10">
              <div
                class="d-none d-sm-flex mt-10 mb-8 flex-lg-nowrap flex-sm-wrap"
              >
                <div>
                  <img class="logo" src="@/assets/PaCE_AgedCare_Logo_RGB.svg" />
                </div>
                <a
                  class="black--text ml-auto mr-3 mt-3 signin-link text-right"
                  v-if="user"
                >
                  <span><small>{{ userInfo }}</small></span>
                  <br />
                  <span class="v-underline" @click="logout">Sign out ></span>
                </a>
                <a
                  @click="login"
                  class="v-underline ml-auto mr-3 black--text signin-link"
                  v-else-if="publicAuthEnabled"
                  >Sign in ></a
                >
              </div>

              <div>
                <!-- Top-level navigation mode toggle -->
                <div class="greeting-mode-toggle mt-2 mb-4">
                  <v-btn-toggle v-model="greetingMode" mandatory color="primary">
                    <v-btn small value="resources">
                      <v-icon left small>mdi-account-group</v-icon>
                      Resources for
                    </v-btn>
                    <v-btn small value="capabilities">
                      <v-icon left small>mdi-chart-donut</v-icon>
                      Capabilities
                    </v-btn>
                  </v-btn-toggle>
                </div>

                <!-- Resources for view -->
                <template v-if="greetingMode === 'resources'">
                  <!-- Step 1: Role selection -->
                  <div v-if="resourcesStep === 'role'">
                    <p class="statement text-wrap">What are you looking for today?</p>
                    <h4 class="tab-title mt-4">Resources for:</h4>
                    <div class="mt-4">
                      <v-list>
                        <v-list-item-group v-model="selectedRoleIndex" mandatory color="pace_grey">
                          <v-list-item
                            v-for="(item, i) in capabilityCodesV1"
                            :key="i"
                            @click="selectRole(i)"
                          >
                            <v-list-item-avatar size="65">
                              <span class="white--text headline">{{ item.short | changeHPtoRNFormat }}</span>
                            </v-list-item-avatar>
                            <v-list-item-content class="my-auto">
                              <p class="v1-role-text">{{ item.name }}</p>
                            </v-list-item-content>
                          </v-list-item>
                        </v-list-item-group>
                      </v-list>
                    </div>
                    <div class="d-block d-sm-flex align-center mb-4 ml-4">
                      <div>
                        <span class="mr-3">None of above?</span>
                        <a @click="goToSearch(true)" class="link">Skip to search</a>
                      </div>
                    </div>
                  </div>

                  <!-- Step 2: Capability selection for selected role -->
                  <div v-else-if="resourcesStep === 'capabilities' && selectedRoleIndex !== null">
                    <!-- Non-wheel roles: flat capability list -->
                    <template v-if="!capabilityCodesV1[selectedRoleIndex].isWheelElement">
                      <h4 class="tab-title mt-4">Which capabilities would you like to develop?</h4>
                      <p>Your selection will be highlighted in green, tap to deselect.</p>
                      <v-list>
                        <v-list-item-group v-model="selectedV1Capabilities" multiple mandatory color="pace_grey">
                          <v-list-item
                            v-for="(item, i) in capabilityCodesV1[selectedRoleIndex].items"
                            :key="i"
                          >
                            <v-list-item-avatar size="65">
                              <span class="white--text headline">
                                {{ capabilityCodesV1[selectedRoleIndex].short | changeHPtoRNFormat }}{{ i + 1 }}
                              </span>
                            </v-list-item-avatar>
                            <v-list-item-content class="my-auto">
                              <p class="subtext mb-0">{{ item.description }}</p>
                            </v-list-item-content>
                          </v-list-item>
                        </v-list-item-group>
                      </v-list>
                    </template>

                    <!-- Wheel roles (ACO, AHP): ElementDoughnutChart -->
                    <template v-else>
                      <h4 class="tab-title mt-4">Select Elements</h4>
                      <div>
                        <span class="text-caption font-italic">Click on Elements of Chart</span>
                      </div>
                      <ElementDoughnutChart
                        :selectedCapabilities="selectedV1Capabilities"
                        @onSelect="onSelectWheel"
                      />
                    </template>

                    <div class="d-block d-sm-flex align-center mb-4 mt-4">
                      <v-btn class="ml-3 mr-3" text @click="resourcesStep = 'role'">
                        <v-icon>mdi-arrow-left</v-icon> back
                      </v-btn>
                      <div class="text-right">
                        <span class="selected-count mr-2">
                          View {{ selectedV1Capabilities && selectedV1Capabilities.length || '0' }}
                          of {{ capabilityCodesV1[selectedRoleIndex].items.length }} selected
                        </span>
                        <v-btn
                          color="bg-eldac-yellow"
                          fab
                          small
                          :disabled="!selectedV1Capabilities || selectedV1Capabilities.length === 0"
                          @click="goToSearchV1"
                        >
                          <v-icon color="white">mdi-chevron-right</v-icon>
                        </v-btn>
                      </div>
                    </div>
                  </div>
                </template>

                <!-- Capabilities view -->
                <template v-else>
                  <p class="statement text-wrap">
                    Which capabilities would you like to develop?
                  </p>

                  <!-- Donut View -->
                  <CapabilityDonut
                    v-if="viewMode === 'donut'"
                    :value="selectedTicklistItems"
                    :groups="groups"
                    :items="items"
                    hub-title="ELDAC Capabilities"
                    hub-sub="End of Life Directions for Aged Care"
                    :outer-radius="460"
                    :inner-radius="230"
                    :group-band="90"
                    :gap-degrees="4"
                    :start-angle="-110"
                    @select="onDonutSelect"
                  />

                  <!-- Toggle between donut and ticklist views -->
                  <div class="view-toggle mt-3 mb-4">
                    <v-btn-toggle v-model="viewMode" mandatory color="primary">
                      <v-btn small value="donut">
                        <v-icon left small>mdi-chart-donut</v-icon>
                        Interactive View
                      </v-btn>
                      <v-btn small value="ticklist">
                        <v-icon left small>mdi-format-list-checks</v-icon>
                        List View
                      </v-btn>
                    </v-btn-toggle>
                  </div>

                  <!-- Ticklist View -->
                  <CapabilityTicklist
                    v-if="viewMode === 'ticklist'"
                    :value="selectedTicklistItems"
                    :items="items"
                    hub-title="ELDAC Palliative Care Capabilities"
                    hub-subtitle="Select the capabilities you want to develop"
                    @change="onTicklistChange"
                  />

                  <!-- Shared Selection Summary -->
                  <SelectionSummary
                    :selected-items="selectedTicklistItems"
                    :all-items="items"
                    @continue-clicked="goToSearch(false)"
                  />

                  <div class="d-block d-sm-flex align-center mb-4 ml-4">
                    <div>
                      <span class="mr-3">Skip capability selection?</span>
                      <a @click="goToSearch(true)" class="link">Go to search</a>
                    </div>
                  </div>
                </template>
              </div>

              <div class="d-flex justify-space-between mt-3 mb-5" v-if="isMobile">
                <img class="logo" src="@/assets/PaCE_AgedCare_Logo_RGB.svg" />
              </div>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { capabilityCodes, capabilityCodesV1 } from "@/data/capabilitycodes";
import { mapGetters, mapActions } from "vuex";
import userInfoDisplayMixin from "@/mixins/userInfoDisplayMixin";
import publicAuthMixin from "@/mixins/publicAuthMixin";
import CapabilityDonut from "@/components/CapabilityDonut.vue";
import CapabilityTicklist from "@/components/CapabilityTicklist.vue";
import SelectionSummary from "@/components/SelectionSummary.vue";
import ElementDoughnutChart from "@/components/chartComponents/ElementDoughnutChart";

export default {
  name: "Greeting",

  mixins: [userInfoDisplayMixin, publicAuthMixin],

  components: {
    CapabilityDonut,
    CapabilityTicklist,
    SelectionSummary,
    ElementDoughnutChart,
  },

  data: () => ({
    greetingMode: "capabilities",
    // V2 capabilities state
    selectedTicklistItems: [],
    viewMode: "donut",
    // V1 resources-for state
    resourcesStep: "role",
    selectedRoleIndex: null,
    selectedV1Capabilities: null,
    capabilityCodesV1,
    isMobile: null,
    groups: [
      { name: "Knowing", color: "#7A3C9A", textColor: "#fff", flip: false },
      { name: "Being",   color: "#3DB09E", textColor: "#fff", flip: false },
      { name: "Doing",   color: "#3F95C8", textColor: "#fff", flip: true },
    ],
    items: capabilityCodes,
  }),

  computed: {
    ...mapGetters("auth", ["user"]),
  },

  methods: {
    ...mapActions(["toggleAuthModal"]),

    onDonutSelect({ selectedIndices }) {
      this.selectedTicklistItems = selectedIndices;
    },

    onTicklistChange({ selected }) {
      this.selectedTicklistItems = selected;
    },

    selectRole(index) {
      this.selectedRoleIndex = index;
      this.selectedV1Capabilities = null;
      this.resourcesStep = "capabilities";
    },

    onSelectWheel(items) {
      this.selectedV1Capabilities = items;
    },

    goToSearchV1() {
      localStorage.setItem("selectedResource", this.selectedRoleIndex);
      localStorage.setItem("selectedCapabilities", JSON.stringify(this.selectedV1Capabilities));

      // Match production V1 behavior: capability selection does not force a profession filter.
      const existingFilters = JSON.parse(localStorage.getItem("filters")) || {};
      localStorage.setItem("filters", JSON.stringify({
        ...existingFilters,
        tagFilterAudienceIds: []
      }));

      this.$router.push({ name: "Search" });
    },

    goToSearch(skip = false) {
      if (!skip) {
        localStorage.setItem(
          "selectedCapabilities",
          JSON.stringify(this.selectedTicklistItems)
        );
        localStorage.removeItem("selectedResource");
      }
      this.$router.push({ name: "Search" });
    },

    logout() {
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      window.location.reload();
    },

    login() {
      this.toggleAuthModal("LOGIN");
    },
  },

  mounted() {
    const saved = JSON.parse(localStorage.getItem("selectedCapabilities"));
    if (Array.isArray(saved)) {
      this.selectedTicklistItems = saved;
    }

    const savedResource = parseInt(localStorage.getItem("selectedResource"));
    if (savedResource > -1 && !isNaN(savedResource)) {
      this.selectedRoleIndex = savedResource;
      this.selectedV1Capabilities = JSON.parse(localStorage.getItem("selectedCapabilities"));
    }

    if (this.$route.query.tab) {
      this.greetingMode = this.$route.query.tab == 2 ? "resources" : "capabilities";
    }

    this.isMobile = window.innerWidth < 600;
  },
};
</script>

<style lang="scss" scoped>
.logo {
  max-width: 369px;
  max-height: 70px;
  width: 100%;
  height: auto;
}

.statement {
  font-size: 17px;
}

.link {
  text-decoration: underline;
}

.left-panel {
  background: #04748e;
  position: relative;

  div {
    background-image: url("../../assets/ELDAC-SignIn.jpg");
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    height: 100%;
    width: 100%;
    opacity: 0.6;
  }
}

.greeting-mode-toggle {
  display: flex;
  justify-content: center;

  .v-btn-toggle {
    background-color: #f5f5f5;
    border-radius: 8px;
    overflow: hidden;
  }

  .v-btn {
    text-transform: none;
    font-weight: 600;
  }
}

.tab-title {
  font-size: 24px;
}

.v1-role-text {
  font-size: 19px;
}

.subtext {
  font-size: 14px;
}

.selected-count {
  font-size: 17px;
  font-weight: bold;
}

.v-list-item {
  max-width: 400px;
  height: 115px;
  align-items: flex-start;
  background-color: #6f716f;
  margin-bottom: 7px;
  padding-left: 12px;

  .v-list-item__avatar {
    margin-right: 12px;
    border-radius: 50%;
    background-color: #147d72;
  }

  .v-list-item__content {
    color: white;
  }

  &--active {
    background-color: #1d7d72;
    .v-list-item__avatar {
      background-color: #6f716f;
    }
  }
}

.v-list-item__content {
  align-self: flex-start;
}

.view-toggle {
  display: flex;
  justify-content: center;

  .v-btn-toggle {
    background-color: #f5f5f5;
    border-radius: 8px;
    overflow: hidden;
  }

  .v-btn {
    text-transform: none;
    font-weight: 600;
  }
}

@media (min-width: 960px) {
  .left-panel {
    min-height: 600px;
  }
}
</style>
